import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller.ts"
import { IncomingInvoicePurchase } from "@/models/incoming-invoice-purchase.model.ts"
import type { IIncomingInvoicePurchase } from "@/interfaces/api/incoming-invoice-purchase.interface.ts"
import { IncomingInvoicePurchaseRepository } from "@/repositories/incoming-invoice-purchase-repository.ts"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers.ts"
import { useIncomingInvoiceStore } from "@/store/incoming-invoices.store.ts"
import { storeToRefs } from "pinia"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager.ts"
import type { IncomingInvoice } from "@/models/incoming-invoice.model.ts"
import type { Purchase } from "@/models/purchase.model.ts"
import { usePurchaseStore } from "@/store/purchase.store.ts"

export class IncomingInvoicePurchaseController extends GenericModelController<
  IncomingInvoicePurchase,
  IIncomingInvoicePurchase,
  IncomingInvoicePurchaseRepository
> {
  constructor() {
    const incomingInvoicesStore = useIncomingInvoiceStore()
    const purchaseStore = usePurchaseStore()
    const { incomingInvoices } = storeToRefs(incomingInvoicesStore)
    const { allPurchases } = storeToRefs(purchaseStore)

    const {
      pushToRelatedObjects: pushToRelatedIncomingInvoice,
      removeFromRelatedObjects: removeFromRelatedIncomingInvoice
    } = useRelatedObjectsManager(
      incomingInvoices,
      (incomingInvoicePurchase: IncomingInvoicePurchase) => incomingInvoicePurchase.incomingInvoice,
      (incomingVoice: IncomingInvoice) => incomingVoice.id,
      (incomingInvoice: IncomingInvoice) => incomingInvoice.incomingInvoicePurchases
    )

    const {
      pushToRelatedObjects: pushToRelatedPurchase,
      removeFromRelatedObjects: removeFromRelatedPurchase
    } = useRelatedObjectsManager(
      allPurchases,
      (incomingInvoicePurchase: IncomingInvoicePurchase) => incomingInvoicePurchase.purchase,
      (purchase: Purchase) => purchase.id,
      (purchase: Purchase) => purchase.incomingInvoicePurchases
    )

    function pushToRelatedObjects(model: IncomingInvoicePurchase) {
      pushToRelatedIncomingInvoice(model)
      pushToRelatedPurchase(model)
    }

    function removeFromRelatedObjects(model: IncomingInvoicePurchase) {
      removeFromRelatedIncomingInvoice(model)
      removeFromRelatedPurchase(model)
    }

    const storeFunctionsConfig = {
      onCreate: pushToRelatedObjects,
      onDelete: removeFromRelatedObjects
    }

    super(IncomingInvoicePurchase, storeFunctionsConfig, new IncomingInvoicePurchaseRepository())

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      IncomingInvoicePurchase,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects
    )
  }
}
